// CustomerEditModal.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config/api';  // ✅ Correct path

// const API_URL = process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost'   
//   ? 'https://dinitracker-api-production.up.railway.app'   
//   : 'http://localhost:5001';

const CustomerEditModal = ({ isEditing, editFormData, onClose, onSave }) => {
  const [formData, setFormData] = useState(editFormData);
  const [isUpdatingDiniCare, setIsUpdatingDiniCare] = useState(false);
  const [isUpdatingWholesaler, setIsUpdatingWholesaler] = useState(false);

  useEffect(() => {
    // Update form data when editFormData changes
    setFormData(editFormData);
  }, [editFormData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    });
  };

  const handleDiniCareChange = async (e) => {
    const newValue = e.target.checked ? 1 : 0;

    // Update form data immediately for UI responsiveness
    setFormData({
      ...formData,
      bDiniCare: newValue,
    });

    // Update the customer's bDiniCare status in the database immediately
    if (formData.ID) {
      try {
        setIsUpdatingDiniCare(true);
        const response = await axios.put(`${API_URL}/api/customers/${formData.ID}/dini-care`, {
          bDiniCare: newValue
        });

        if (!response.data.success) {
          console.error('Failed to update DiNi Care status:', response.data);
          alert('Failed to update DiNi Care status');
          // Revert the change
          setFormData({
            ...formData,
            bDiniCare: newValue === 1 ? 0 : 1,
          });
        }
      } catch (error) {
        console.error('Error updating DiNi Care status:', error);
        alert(`Failed to update DiNi Care status: ${error.message}`);
        // Revert the change
        setFormData({
          ...formData,
          bDiniCare: newValue === 1 ? 0 : 1,
        });
      } finally {
        setIsUpdatingDiniCare(false);
      }
    }
  };

  const handleWholesalerChange = async (e) => {
    const newValue = e.target.checked ? 1 : 0;

    setFormData({ ...formData, isWholesaler: newValue });

    if (formData.ID) {
      try {
        setIsUpdatingWholesaler(true);
        const response = await axios.put(`${API_URL}/api/customers/${formData.ID}/wholesaler`, {
          isWholesaler: newValue
        });

        if (!response.data.success) {
          setFormData({ ...formData, isWholesaler: newValue === 1 ? 0 : 1 });
          alert('Failed to update Wholesaler status');
        }
      } catch (error) {
        setFormData({ ...formData, isWholesaler: newValue === 1 ? 0 : 1 });
        alert(`Failed to update Wholesaler status: ${error.message}`);
      } finally {
        setIsUpdatingWholesaler(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      console.log('Sending update to API:', formData);
      
      // Send to API
      const response = await axios.post(`${API_URL}/api/customers`, formData);
      
      if (response.data && response.data.success) {
        console.log('Customer update API response:', response.data);
        
        // Format the data for the parent component
        const updatedCustomerData = {
          ...formData,
          // Convert the form data back to the format expected by the parent
          customerName: `${formData.CustomerFirstName} ${formData.CustomerLastName}`.trim(),
          phone: formData.CustomerPhone,
          email: formData.CustomerEmail,
          address: formData.CustomerAddress,
          city: formData.CustomerCity,
          state: formData.CustomerState,
          zip: formData.CustomerZipCode,
          moldId: formData.MoldId // 🆕 NEW: Include MoldId in the updated data
        };
        
        // Call the onSave callback with both the form data and UI-friendly data
        onSave(formData, updatedCustomerData);
      } else {
        console.error('API returned error:', response.data);
        alert(`Error: ${response.data?.error || 'Failed to update customer'}`);
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      alert(`An error occurred while saving customer information: ${error.message}`);
    }
  };
  if (!isEditing) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">Edit Customer Information</h5>
          <button type="button" className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">First Name</label>
                <input
                  type="text"
                  className="form-control"
                  name="CustomerFirstName"
                  value={formData.CustomerFirstName || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Last Name</label>
                <input
                  type="text"
                  className="form-control"
                  name="CustomerLastName"
                  value={formData.CustomerLastName || ''}
                  onChange={handleChange}
                />
              </div>
            </div>
            
            <div className="mb-3">
              <label className="form-label">Phone</label>
              <input
                type="tel"
                className="form-control"
                name="CustomerPhone"
                value={formData.CustomerPhone || ''}
                onChange={handleChange}
              />
            </div>
            
            <div className="mb-3">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-control"
                name="CustomerEmail"
                value={formData.CustomerEmail || ''}
                onChange={handleChange}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Mold ID</label>
              <input
                type="text"
                className="form-control"
                name="MoldId"
                value={formData.MoldId || ''}
                onChange={handleChange}
                placeholder="Enter Mold ID (optional)"
              />
            </div>


            <div className="mb-3">
              <label className="form-label">Billing Address</label>
              <input
                type="text"
                className="form-control"
                name="CustomerAddress"
                value={formData.CustomerAddress || ''}
                onChange={handleChange}
              />
            </div>
            
            <div className="row">
              <div className="col-md-5 mb-3">
                <label className="form-label">City</label>
                <input
                  type="text"
                  className="form-control"
                  name="CustomerCity"
                  value={formData.CustomerCity || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">State</label>
                <input
                  type="text"
                  className="form-control"
                  name="CustomerState"
                  value={formData.CustomerState || ''}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Zip Code</label>
                <input
                  type="text"
                  className="form-control"
                  name="CustomerZipCode"
                  value={formData.CustomerZipCode || ''}
                  onChange={handleChange}
                />
              </div>
            <div className="mb-3">
              <label className="form-label checkbox-container">
                <input
                  type="checkbox"
                  name="bDiniCare"
                  checked={formData.bDiniCare === 1 || formData.bDiniCare === true}
                  onChange={handleDiniCareChange}
                  disabled={isUpdatingDiniCare}
                  style={{ marginRight: '10px', width: 'auto', cursor: 'pointer' }}
                  
                /> DiNi Care Customer
                <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {isUpdatingDiniCare && <i className="fas fa-spinner fa-spin"></i>}
                  {(formData.bDiniCare === 1 || formData.bDiniCare === true) && (
                    <img
                      src="/images/dini_cares.gif"
                      alt="DiNi Care"
                      style={{ width: '160px', height: '100px', alignItems: 'flex-end' }}
                    />
                  )}
                </span>
              </label>
            </div>
            <div className="mb-3">
              <label className="form-label checkbox-container">
                <input
                  type="checkbox"
                  name="isWholesaler"
                  checked={formData.isWholesaler === 1 || formData.isWholesaler === true}
                  onChange={handleWholesalerChange}
                  disabled={isUpdatingWholesaler}
                  style={{ marginRight: '2px', width: 'auto', cursor: 'pointer' }}
                /> Wholesaler
                {isUpdatingWholesaler && <i className="fas fa-spinner fa-spin" style={{ marginLeft: '6px' }}></i>}
              </label>
            </div>
            
            </div>
            
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary"
              style={{ color: 'white' }}
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Modal styling - to match your existing modal styles */}
      <style jsx>{`
/* Updated modal styles with better dark mode support */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: var(--dark-surface, #1a1a1a);
  color: var(--text-primary, #ffffff);
  border-radius: 8px;
  width: 100%;
  max-width: 600px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid var(--dark-border, #2c2c2c);
}

.modal-header {
  padding: 15px 20px;
  border-bottom: 1px solid var(--dark-border, #2c2c2c);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--dark-elevated, #262626);
}

.modal-title {
  margin: 0;
  line-height: 1.5;
  color: var(--mfg-dini-pink, #FF68C5);
  font-size: 1.25rem;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--text-secondary, #cccccc);
  transition: color 0.2s ease;
}

.close-btn:hover {
  color: var(--text-primary, #ffffff);
}

.modal-body {
  padding: 20px;
  background-color: var(--dark-surface, #1a1a1a);
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid var(--dark-border, #2c2c2c);
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background-color: var(--dark-elevated, #262626);
}

/* Form label styling - MOST IMPORTANT FIX */
.form-label {
  margin-bottom: 0.5rem;
  color: var(--text-primary, #ffffff) !important;
  font-weight: 500;
  display: inline-block;
  font-size: 0.9rem;
}

/* Form controls with fallback colors */
.form-control {
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  font-size: 1rem;
  line-height: 1.5;
  color: var(--text-primary, #ffffff) !important;
  background-color: var(--dark-elevated, #262626) !important;
  background-clip: padding-box;
  border: 1px solid var(--dark-border, #2c2c2c);
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
  color: var(--text-primary, #ffffff) !important;
  background-color: var(--dark-elevated, #262626) !important;
  border-color: var(--purple-accent, #4027b0);
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(64, 39, 176, 0.25);
}

.form-control::placeholder {
  color: var(--text-secondary, #999999);
  opacity: 1;
}

/* Button styling */
.btn {
  display: inline-block;
  padding: 0.5rem 1rem;
  margin-bottom: 0;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5;
  text-align: center;
  white-space: nowrap;
  vertical-align: middle;
  cursor: pointer;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  text-decoration: none;
  transition: all 0.15s ease-in-out;
}

.btn-primary {
  background-color: var(--purple-accent, #4027b0);
  border-color: var(--purple-accent, #4027b0);
  color: #ffffff;
}

.btn-primary:hover {
  background-color: var(--purple-light, #bb86fc);
  border-color: var(--purple-light, #bb86fc);
  color: #000000;
}

.btn-secondary {
  background-color: var(--dark-elevated, #262626);
  border-color: var(--dark-border, #2c2c2c);
  color: var(--text-primary, #ffffff);
}

.btn-secondary:hover {
  background-color: var(--dark-border, #2c2c2c);
  border-color: var(--dark-border, #2c2c2c);
}

/* Grid system */
.row {
  display: flex;
  flex-wrap: wrap;
  margin-right: -15px;
  margin-left: -15px;
}

.col-md-6, .col-md-5, .col-md-4, .col-md-3 {
  position: relative;
  width: 100%;
  padding-right: 15px;
  padding-left: 15px;
}

.col-md-6 {
  flex: 0 0 50%;
  max-width: 50%;
}

.col-md-5 {
  flex: 0 0 41.666667%;
  max-width: 41.666667%;
}

.col-md-4 {
  flex: 0 0 33.333333%;
  max-width: 33.333333%;
}

.col-md-3 {
  flex: 0 0 25%;
  max-width: 25%;
}

.mb-3 {
  margin-bottom: 1rem;
}

/* Responsive design for mobile */
@media (max-width: 576px) {
  .modal-content {
    width: 95%;
    margin: 0 10px;
  }
  
  .row {
    margin-right: -5px;
    margin-left: -5px;
  }
  
  .col-md-6, .col-md-5, .col-md-4, .col-md-3 {
    flex: 0 0 100%;
    max-width: 100%;
    padding-right: 5px;
    padding-left: 5px;
  }
}

/* Force dark mode for the modal specifically */
.modal-content * {
  color: inherit;
}

.modal-content input {
  color: var(--text-primary, #ffffff) !important;
  background-color: var(--dark-elevated, #262626) !important;
}
      `}</style>
    </div>
  );
};

export default CustomerEditModal;